// @ts-ignore
import React from "react";

import { css } from "@compiled/react";
import { useLocalizedHref } from "@libraries/web-framework";
import { useIntl } from "react-intl";

import { media } from "@atlaskit/primitives/responsive";

import { LanguageSelector } from "../../../components/global-footer/language-selector/src";
import SocialLinks from "../../../components/global-footer/social-links/src";
import { LinkWithIcon } from "../../../components/global-nav/list-of-links/src";
import Link from "../../../components/wpl/link/src/index";

// ==================================================================
// CSS styling
// ==================================================================
const containerStyle = css({
    borderBlockStart: "1px solid #dfe1e5",
    containerType: "inline-size"
});

const rowStyle = css({
    maxWidth: "1440px",
    marginInlineStart: "auto",
    marginInlineEnd: "auto"
});

const columnStyle = css({
    fontSize: "0.875rem",
    display: "flex",
    flexWrap: "wrap",
    [media.below.sm]: {
        padding: "16px",
        flexDirection: "column",
        justifyContent: "flex-start",
        alignItems: "flex-start",
        rowGap: "5px"
    },
    [media.only.sm]: {
        flexDirection: "row",
        alignItems: "center",
        padding: "16px",
        columnGap: "1rem"
    },
    [media.above.md]: {
        flexDirection: "row",
        alignItems: "center",
        marginInlineStart: "8.33333%",
        width: "83.66666%",
        columnGap: "1.5rem"
    }
});

const languageSelectorStyle = css({
    [media.below.sm]: {
        marginBlockEnd: "-5px"
    }
});

const linkStyle = css({
    "height": "26px",
    "textDecoration": "none",
    ":hover": {
        textDecoration: "underline",
        textShadow: "none"
    },
    ":visited, :focus": {
        textShadow: "none"
    },
    [media.below.sm]: {
        marginInlineStart: "2px"
    }
});

const copyrightStyle = css({
    color: "#42526e",
    height: "26px",
    lineHeight: "26px",
    [media.below.sm]: {
        marginInlineStart: "2px"
    }
});

const socialLinksStyle = css({
    alignSelf: "flex-end",
    [media.above.sm]: {
        marginInlineStart: "auto"
    }
});

// ==================================================================
// Type Props
// ==================================================================
export interface GlobalFooterBottomProps {
    selectedLanguage?: string;
    onLanguageChangeHandler?: React.Dispatch<string>;
    className?: string;
}

// ==================================================================
// Rendering Functions
// ==================================================================

export const GlobalFooterBottom = ({
    selectedLanguage,
    onLanguageChangeHandler,
    className
}: GlobalFooterBottomProps) => {
    const intl = useIntl();
    const bottomLinks = [
        {
            text: intl.formatMessage({
                id: "globalFooter.bottom.privacy-policy",
                defaultMessage: "Privacy policy"
            }),
            href: useLocalizedHref("/legal/privacy-policy"),
            isOneTrustWrapped: false
        },
        {
            text: intl.formatMessage({
                id: "globalFooter.bottom.notice-at-collection",
                defaultMessage: "Notice at Collection"
            }),
            href: "/legal/privacy-policy#additional-disclosures-for-ca-residents",
            isOneTrustWrapped: true
        },
        {
            text: intl.formatMessage({
                id: "globalFooter.bottom.terms",
                defaultMessage: "Terms"
            }),
            href: "/legal/cloud-terms-of-service",
            isOneTrustWrapped: false
        },
        {
            text: intl.formatMessage({
                id: "globalFooter.bottom.impressum",
                defaultMessage: "Impressum"
            }),
            href: "/legal/impressum",
            isOneTrustWrapped: false
        }
    ];
    const copyRightYear = new Date().getFullYear();

    return (
        <div className={className} css={containerStyle}>
            <div css={rowStyle}>
                <div css={columnStyle}>
                    <LanguageSelector
                        selectedLanguage={selectedLanguage}
                        onLanguageChangeHandler={onLanguageChangeHandler}
                        css={languageSelectorStyle}
                    />
                    {bottomLinks.map((bottomLink) => {
                        if (bottomLink.isOneTrustWrapped) {
                            return (
                                <div
                                    key={bottomLink.text}
                                    className={
                                        "link-wrapper atl-policy-link-container"
                                    }
                                >
                                    <LinkWithIcon
                                        linkText={bottomLink.text}
                                        href={bottomLink.href}
                                        type="anchor"
                                        isHaveIcon={false}
                                        isUsePlainSpanTagForLinkText={true}
                                        className={"atl-policy-link"}
                                        spanLinkTextClassName={
                                            "atl-policy-link-text"
                                        }
                                    />
                                </div>
                            );
                        } else {
                            return (
                                <Link
                                    key={bottomLink.text}
                                    text={bottomLink.text}
                                    href={bottomLink.href}
                                    type="anchor"
                                    customLinkColor={"#42526e"}
                                    css={linkStyle}
                                />
                            );
                        }
                    })}
                    <div css={copyrightStyle}>
                        Copyright © {copyRightYear} Atlassian
                    </div>
                    <SocialLinks css={socialLinksStyle} />
                </div>
            </div>
        </div>
    );
};
